/**
 * EdgeContextMenu - Menú contextual para edges de la red canónica
 *
 * @version 2.0
 * @created 2026-03-16
 * @updated 2026-04-13
 * @module core/spatial/components
 *
 * @description
 * Menú contextual que aparece al hacer click derecho sobre un edge.
 * Organiza las opciones en dos categorías:
 *
 * **Operaciones Geométricas** (no alteran topología):
 * - Editar forma: modifica shape points internos
 * - Simplificar a recta: elimina shape points, deja solo extremos
 *
 * **Operaciones Topológicas** (alteran estructura de la red):
 * - Insertar nodo: divide el segmento en dos, crea nodo nuevo
 * - Eliminar segmento: remueve el edge de la red
 */

import { useEffect, useRef } from 'react';
import { Trash2, CirclePlus, PenLine, Minus, GitMerge } from 'lucide-react';
import { mapEngine } from '../../map-engine/MapEngineService';
import './edge-context-menu.css';

export interface EdgeContextMenuProps {
  /** Coordenadas [lng, lat] donde mostrar el menú */
  position: [number, number];
  /** ID del edge seleccionado */
  edgeId: string;
  /** Indica si el edge ya es recto (solo 2 puntos) - deshabilita "Simplificar" */
  isAlreadyStraight: boolean;
  /** Callback al seleccionar "Editar forma" (geométrico) */
  onEditShape: () => void;
  /** Callback al seleccionar "Simplificar a recta" (geométrico) */
  onSimplify: () => void;
  /** Callback al seleccionar "Insertar nodo" (topológico) */
  onInsertNode: () => void;
  /** Callback al seleccionar "Agregar a selección múltiple" (Multi-Merge V1) */
  onAddToSelection: () => void;
  /** Callback al seleccionar "Eliminar segmento" (destructivo) */
  onDelete: () => void;
  /** Callback al cerrar el menú */
  onClose: () => void;
}

/**
 * Componente EdgeContextMenu
 */
export function EdgeContextMenu({
  position,
  edgeId: _edgeId, // Unused but kept for interface consistency
  isAlreadyStraight,
  onEditShape,
  onSimplify,
  onInsertNode,
  onAddToSelection,
  onDelete,
  onClose,
}: EdgeContextMenuProps): JSX.Element {
  const menuRef = useRef<HTMLDivElement>(null);

  /**
   * Posicionar menú en coordenadas del mapa
   */
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !menuRef.current) return;

    const updatePosition = (): void => {
      if (!map || !menuRef.current) return;

      const point = map.project([position[0], position[1]]);

      // Offset del TopBar
      const uiScale = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ui-scale')) || 1.0;
      const topBarHeight = 120 * uiScale;

      const adjustedY = point.y + topBarHeight;

      // Posicionar ligeramente arriba y a la derecha del click
      menuRef.current.style.left = `${point.x + 8}px`;
      menuRef.current.style.top = `${adjustedY - 8}px`;
    };

    updatePosition();

    map.on('move', updatePosition);
    map.on('zoom', updatePosition);

    return () => {
      map.off('move', updatePosition);
      map.off('zoom', updatePosition);
    };
  }, [position]);

  /**
   * Cerrar con ESC
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  /**
   * Cerrar al hacer click fuera
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Pequeño delay para no capturar el click que abrió el menú
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 50);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div ref={menuRef} className="edge-context-menu">
      {/* ═══ OPERACIONES GEOMÉTRICAS ═══ */}
      <button
        className="edge-context-menu-item"
        onClick={() => {
          onEditShape();
        }}
      >
        <PenLine size={16} />
        <span>Editar forma</span>
      </button>
      <button
        className={`edge-context-menu-item ${isAlreadyStraight ? 'edge-context-menu-item--disabled' : ''}`}
        onClick={() => {
          if (!isAlreadyStraight) {
            onSimplify();
          }
        }}
        disabled={isAlreadyStraight}
        title={isAlreadyStraight ? 'El segmento ya es recto' : 'Eliminar puntos internos'}
      >
        <Minus size={16} />
        <span>Simplificar a recta</span>
      </button>

      <div className="edge-context-menu-divider" />

      {/* ═══ OPERACIONES TOPOLÓGICAS ═══ */}
      <button
        className="edge-context-menu-item"
        onClick={() => {
          onInsertNode();
        }}
      >
        <CirclePlus size={16} />
        <span>Insertar nodo</span>
      </button>

      <button
        className="edge-context-menu-item"
        onClick={() => {
          onAddToSelection();
        }}
        title="Agregar este segmento a la selección múltiple para fusionar (Multi-Merge V1)"
      >
        <GitMerge size={16} />
        <span>Agregar a selección múltiple</span>
      </button>

      <div className="edge-context-menu-divider" />

      {/* ═══ OPERACIÓN DESTRUCTIVA ═══ */}
      <button
        className="edge-context-menu-item edge-context-menu-item--danger"
        onClick={() => {
          onDelete();
        }}
      >
        <Trash2 size={16} />
        <span>Eliminar segmento</span>
      </button>
    </div>
  );
}
